import React from 'react'
import { Button, Card, Flex, Input, Segmented, Spin, Switch, Tag, Typography } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { AssistantVoice, VoiceInput } from '../site'

type VoiceProfile = AssistantVoice['profile']

type SiteVoiceProps = {
    value: VoiceInput
    onChange: (value: VoiceInput) => void
    profile: VoiceProfile
    editable: boolean
    analyzing: boolean
    onAnalyze: () => void
}

export const MAX_SUGGESTIONS = 3

/**
 * How Evaa talks: like the website (what Evaa read from it) or a chosen tone, the company's own instructions, what
 * Evaa does when it does not know, and the questions suggested under the welcome message.
 */
export function SiteVoice({ value, onChange, profile, editable, analyzing, onAnalyze }: SiteVoiceProps) {
    const { t } = useTranslation()

    const set = (patch: Partial<VoiceInput>) =>
        onChange({
            ...value,
            ...patch,
        })

    return (
        <Card title={t('How Evaa talks')}>
            <Flex
                vertical
                gap="large"
            >
                <Flex
                    vertical
                    gap="small"
                >
                    <Typography.Text strong>{t('Tone')}</Typography.Text>
                    <Segmented
                        value={value.tone}
                        disabled={!editable}
                        onChange={(tone) => set({ tone: tone as VoiceInput['tone'] })}
                        options={[
                            {
                                label: t('Like your website'),
                                value: 'website',
                            },
                            {
                                label: t('Choose yourself'),
                                value: 'custom',
                            },
                        ]}
                    />
                    {value.tone === 'website' ? (
                        <div className="rounded-lg bg-neutral-50 px-3 py-2">
                            <WebsiteTone
                                profile={profile}
                                editable={editable}
                                analyzing={analyzing}
                                onAnalyze={onAnalyze}
                            />
                        </div>
                    ) : (
                        <CustomTone
                            value={value}
                            editable={editable}
                            onChange={set}
                        />
                    )}
                </Flex>

                <Instructions
                    value={value}
                    editable={editable}
                    onChange={set}
                />

                <Flex
                    vertical
                    gap={4}
                >
                    <Typography.Text strong>{t('When Evaa does not know the answer')}</Typography.Text>
                    <Input.TextArea
                        value={value.fallback ?? ''}
                        rows={2}
                        maxLength={500}
                        disabled={!editable}
                        placeholder={t(
                            'For example: Call Petra from recruitment on 078 123 45 67, or email werken@yourcompany.com.'
                        )}
                        onChange={(event) => set({ fallback: event.target.value || null })}
                    />
                    <Typography.Text type="secondary">
                        {t('Evaa says so honestly, and then passes this on.')}
                    </Typography.Text>
                </Flex>

                <Suggestions
                    value={value}
                    profile={profile}
                    editable={editable}
                    onChange={set}
                />
            </Flex>
        </Card>
    )
}

type WebsiteToneProps = {
    profile: VoiceProfile
    editable: boolean
    analyzing: boolean
    onAnalyze: () => void
}

/**
 * The tone Evaa read from the website.
 */
function WebsiteTone({ profile, editable, analyzing, onAnalyze }: WebsiteToneProps) {
    const { t } = useTranslation()

    if (analyzing) {
        return (
            <Flex
                gap="small"
                align="center"
            >
                <Spin size="small" />
                <Typography.Text type="secondary">{t('Evaa is reading the tone of your website…')}</Typography.Text>
            </Flex>
        )
    }

    if (!profile) {
        return (
            <Typography.Text type="secondary">
                {t('When Evaa has read your pages, it chooses the tone of your website.')}
            </Typography.Text>
        )
    }

    return (
        <Flex
            vertical
            gap={4}
        >
            <Typography.Text>{profile.summary}</Typography.Text>
            {profile.example && (
                <Typography.Text
                    type="secondary"
                    italic
                >
                    {t('For example:')} “{profile.example}”
                </Typography.Text>
            )}
            {editable && (
                <div>
                    <Button
                        type="link"
                        size="small"
                        className="px-0"
                        icon={<ReloadOutlined />}
                        onClick={onAnalyze}
                    >
                        {t('Read the tone again')}
                    </Button>
                </div>
            )}
        </Flex>
    )
}

type VoiceSectionProps = {
    value: VoiceInput
    editable: boolean
    onChange: (patch: Partial<VoiceInput>) => void
}

function CustomTone({ value, editable, onChange }: VoiceSectionProps) {
    const { t } = useTranslation()

    return (
        <Flex
            vertical
            gap="small"
        >
            <Flex
                justify="space-between"
                align="center"
                gap="small"
                wrap
            >
                <Typography.Text>{t('Form of address')}</Typography.Text>
                <Segmented
                    value={value.address}
                    disabled={!editable}
                    onChange={(address) => onChange({ address: address as VoiceInput['address'] })}
                    options={[
                        {
                            label: t('Informal (je)'),
                            value: 'je',
                        },
                        {
                            label: t('Formal (u)'),
                            value: 'u',
                        },
                    ]}
                />
            </Flex>
            <Flex
                justify="space-between"
                align="center"
                gap="small"
                wrap
            >
                <Typography.Text>{t('Style')}</Typography.Text>
                <Segmented
                    value={value.style}
                    disabled={!editable}
                    onChange={(style) => onChange({ style: style as VoiceInput['style'] })}
                    options={[
                        {
                            label: t('Short and clear'),
                            value: 'short',
                        },
                        {
                            label: t('Warm and personal'),
                            value: 'warm',
                        },
                    ]}
                />
            </Flex>
            <Flex
                justify="space-between"
                align="center"
                gap="small"
            >
                <Typography.Text>{t('Emoji')}</Typography.Text>
                <Switch
                    checked={value.emoji}
                    disabled={!editable}
                    onChange={(emoji) => onChange({ emoji })}
                />
            </Flex>
        </Flex>
    )
}

/**
 * The company's own rules for how Evaa answers, with examples to add in one click.
 */
function Instructions({ value, editable, onChange }: VoiceSectionProps) {
    const { t } = useTranslation()
    const instructions = value.instructions ?? ''

    const examples = [
        t('Refer to the collective labour agreement for questions about salary.'),
        t('Mention that we are flexible with schedules around school hours.'),
        t('Do not talk about other organisations.'),
    ]

    const unusedExamples = examples.filter((example) => !instructions.includes(example))

    const addExample = (example: string) =>
        onChange({
            instructions: [value.instructions?.trim(), example].filter(Boolean).join('\n'),
        })

    return (
        <Flex
            vertical
            gap={4}
        >
            <Typography.Text strong>{t('What else should Evaa know or do?')}</Typography.Text>
            <Input.TextArea
                value={instructions}
                rows={4}
                maxLength={2000}
                disabled={!editable}
                placeholder={t('For example: Refer to the collective labour agreement for questions about salary.')}
                onChange={(event) => onChange({ instructions: event.target.value || null })}
            />
            <Typography.Text type="secondary">
                {t('Rules for how Evaa answers. You add facts as an answer under Knowledge.')}
            </Typography.Text>
            {editable && (
                <Flex
                    gap={6}
                    wrap
                >
                    {unusedExamples.map((example) => (
                        <Tag
                            key={example}
                            className="m-0 cursor-pointer"
                            icon={<PlusOutlined />}
                            onClick={() => addExample(example)}
                        >
                            {example}
                        </Tag>
                    ))}
                </Flex>
            )}
        </Flex>
    )
}

type SuggestionsProps = VoiceSectionProps & {
    profile: VoiceProfile
}

/**
 * The questions under the welcome message: Evaa's own suggestions (null), the company's, or none (an empty list).
 */
function Suggestions({ value, profile, editable, onChange }: SuggestionsProps) {
    const { t } = useTranslation()
    const suggestions = value.suggestions ?? profile?.suggestions ?? []
    const isOn = value.suggestions === null || value.suggestions.length > 0
    const canUseSuggested = Boolean(profile) && editable

    const setSuggestion = (index: number, text: string) => {
        const next = Array.from({ length: MAX_SUGGESTIONS }, (_, current) => suggestions[current] ?? '')
        next[index] = text

        onChange({ suggestions: next })
    }

    return (
        <Flex
            vertical
            gap="small"
        >
            <Flex
                justify="space-between"
                align="center"
                gap="middle"
            >
                <Flex vertical>
                    <Typography.Text strong>{t('Suggested questions in the chat')}</Typography.Text>
                    <Typography.Text type="secondary">
                        {t('Visitors can click them under the welcome message.')}
                    </Typography.Text>
                </Flex>
                <Switch
                    checked={isOn}
                    disabled={!editable}
                    onChange={(on) => onChange({ suggestions: on ? null : [] })}
                />
            </Flex>
            {isOn && (
                <>
                    {Array.from({ length: MAX_SUGGESTIONS }, (_, index) => (
                        <Input
                            key={index}
                            value={suggestions[index] ?? ''}
                            maxLength={100}
                            disabled={!editable}
                            placeholder={t('Question {{number}}', { number: index + 1 })}
                            onChange={(event) => setSuggestion(index, event.target.value)}
                        />
                    ))}
                    <Typography.Text type="secondary">
                        {value.suggestions === null
                            ? t('Suggested by Evaa, based on your website.')
                            : canUseSuggested && (
                                  <Button
                                      type="link"
                                      size="small"
                                      className="px-0"
                                      onClick={() => onChange({ suggestions: null })}
                                  >
                                      {t('Use the questions Evaa suggests')}
                                  </Button>
                              )}
                    </Typography.Text>
                </>
            )}
        </Flex>
    )
}
